import { useCallback, useEffect, useRef, useState } from 'react';
import { useAiTurnControl } from '@hooks/useAiTurnControl';
import { useTranslation } from 'react-i18next';
import { getAiConversation, streamAiChat } from '@apis/aiAgent';
import {
  AI_CUT_OFF_MESSAGE_KEY,
  aiErrorMessageKey,
  aiSendFailureKey,
  dropRefusedTurn,
  settleAnswer,
  type IChatMessage,
  type IChatStatus,
} from '@utils/aiChat';

/**
 * The card's message is the shared `IChatMessage`, drawings included: the
 * transcript decides per width whether a table or chart is drawn inline or
 * pointed at on the full page, so the payload has to be here for both. The
 * optional `id` a locally-appended bubble does not have yet is never read —
 * the card does not rate answers.
 */
export type CardMessage = IChatMessage;

export interface UseAiChat {
  messages: CardMessage[];
  conversationId: string | null;
  isStreaming: boolean;
  /** The most recent `status` frame while the answer has not started — the full page's line. */
  status: IChatStatus | null;
  error: string | null;
  send: (query: string) => Promise<void>;
  /** The reader's stop: cancel the turn in flight and mark its answer stopped. */
  stop: () => void;
  /** Resolves true when the conversation was loaded, false when it no longer
   *  exists upstream (404 → `getAiConversation` returns null). Rejects only on
   *  a genuine network/transport error. */
  restore: (conversationId: string) => Promise<boolean>;
  reset: () => void;
}

/**
 * Card-local chat state machine. Reuses `streamAiChat` / `getAiConversation`
 * from the API layer (which own the SSE loop, frame parsing, and abort/race
 * guards) so none of that is reimplemented here.
 */
export function useAiChat(siteId: string): UseAiChat {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<CardMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<IChatStatus | null>(null);
  // The stream's controller, the answer's writer and the reader's stop — the
  // full page's, so the two surfaces stop a turn the same way.
  const { abortRef, updateLastAssistant, writeAnswer, stop } = useAiTurnControl(setMessages);
  // Synchronous in-flight guard. `isStreaming` state lags a render behind,
  // so a rapid double-submit could start two concurrent streams before the
  // first `setIsStreaming(true)` commits; the ref blocks that synchronously.
  const isStreamingRef = useRef(false);

  // Abort any in-flight stream/fetch when the card unmounts (e.g. navigation).
  useEffect(() => () => abortRef.current?.abort(), [abortRef]);

  const send = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      if (!query || isStreamingRef.current) return;
      isStreamingRef.current = true;
      setError(null);
      setStatus(null);
      // User bubble + an empty assistant bubble we fill as chunks arrive,
      // `unsaved` until `done` says the turn stored it — as on the full page.
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: query },
        { role: 'assistant', content: '', unsaved: true },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      let errored = false;
      try {
        await streamAiChat(
          siteId,
          query,
          conversationId,
          (event) => {
            // The full page's rule: the answer replaces the status line. Its
            // frames go onto it in order — whether the card draws a drawing or
            // nudges to the full page is the transcript's call, at render time.
            if (event.type === 'text') setStatus(null);
            if (writeAnswer(event)) return;
            switch (event.type) {
              case 'metadata':
                if (!conversationId) setConversationId(event.conversationId);
                break;
              // THE CARD SHOWS THE WAIT. It once ignored these — a teaser had
              // no business narrating tool calls — but it is where most first
              // questions are asked, and a first question can sit behind the
              // dots for tens of seconds. The full page's line, the full
              // page's rule: the answer replaces it.
              case 'status':
                setStatus({ label: event.label, failed: event.failed });
                break;
              case 'error':
                // Never `event.message` — the frame's message is internal
                // English prose. The code is what carries a translation.
                errored = true;
                setError(t(aiErrorMessageKey(event.code)));
                break;
              // The card renders no truncation marker (it belongs with the
              // full transcript), but a deadline-aborted turn stored nothing,
              // and the reader must be told. `at` is the receipt's time, as
              // on the full page.
              case 'done':
                updateLastAssistant((last) => ({
                  ...settleAnswer(last, event, errored),
                  at: new Date().toISOString(),
                }));
                if (event.aborted) setError(t(AI_CUT_OFF_MESSAGE_KEY));
                break;
            }
          },
          controller.signal,
        );
      } catch (err) {
        // Swallow aborts (collapse/unmount); surface everything else.
        if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError'))
          return;
        // A refused ask stored nothing, so its bubbles go with it.
        setMessages((prev) => dropRefusedTurn(prev, err));
        setError(t(aiSendFailureKey(err, 'dashboard.aiAgent.errorGeneric')));
      } finally {
        isStreamingRef.current = false;
        setIsStreaming(false);
        setStatus(null);
        abortRef.current = null;
      }
    },
    [abortRef, conversationId, siteId, t, updateLastAssistant, writeAnswer],
  );

  const restore = useCallback(
    async (id: string) => {
      // Cancel any live stream first so it can't keep writing into `messages`
      // after we replace them with the restored thread (abort-before-overwrite).
      abortRef.current?.abort();
      abortRef.current = null;
      // The restore fetch gets its OWN controller and is deliberately NOT stored
      // in abortRef: this is a mount-once load that must survive StrictMode's
      // dev double-invoke (whose unmount cleanup aborts abortRef) and a real
      // unmount (a late resolve then is a harmless no-op). Tracking it in
      // abortRef would let that cleanup cancel the only fetch, and the caller's
      // mount-once guard would then block any retry — leaving the thread empty.
      const controller = new AbortController();
      try {
        const detail = await getAiConversation(siteId, id, controller.signal);
        if (!detail) return false; // 404 — conversation gone upstream; leave id unset.
        setConversationId(id);
        setMessages(detail.messages);
        return true;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return false;
        // Genuine transport error — let the caller fall back (clear + collapse).
        throw err;
      }
    },
    [abortRef, siteId],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    isStreamingRef.current = false;
    setIsStreaming(false);
    setMessages([]);
    setConversationId(null);
    setError(null);
    setStatus(null);
  }, [abortRef]);

  return { messages, conversationId, isStreaming, status, error, send, stop, restore, reset };
}
